import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { Box, Link, Paper, Button, Divider, IconButton, Drawer, List, ListItem, ListItemText, Accordion, AccordionActions, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import Container from '../../../../../components/Container';
import Main from '../../../../../layouts/Main';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import InfoItemBox from './InfoItemBox';
import { InfoItemBoxProps } from '../dataObjects/contentTypes';
import Grid from '@mui/material/Grid2';

const drawerWidth = 800;

interface Props {
    open: boolean; 
    handleDrawerClose: () => void;
}

const content: InfoItemBoxProps[] = [
  {
    title: "Electronic Filing",
    blocks: [
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "text",
                content: "All candidates and committees are required to electronically file their reports using OCPF's E-Filing software - "
              },
              {
                type: "link",
                text: "Reporter 7",
                url: "http://www.ocpfreporter.us/"
              },
              {
                type: "text",
                content: "."
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Once your committee is organized with OCPF, a CPF ID number and an initial password will be issued. The filer uses the issued CPF ID and password to log into "
              },
              {
                type: "link",
                text: "Reporter 7",
                url: "http://www.ocpfreporter.us/"
              },
              {
                type: "text",
                content: "."
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Candidates & committees must maintain detailed accounts of all contributions received and all expenditures made. These records must be kept separate and distinct from any other accounts or records and must be preserved for six years from the relevant election."
              }
            ]
          }
        ]
      }
    ]
  },
  {
    title: "Help Videos To Get Started",
    blocks: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            content: "We've created a "
          },
          {
            type: "link",
            text: "playlist",
            url: "https://www.youtube.com/playlist?list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S"
          },
          {
            type: "text",
            content: " of videos to get you started using Reporter 7. Videos include:"
          }
        ]
      },
      {
        type: "subheading",
        content: "Depository Filers"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "How To File A Deposit Report",
                url: "https://www.youtube.com/watch?v=Ce09jG2PDKQ"
              }
            ]
          },
          {
            content: [
              {
                type: "link",
                text: "How To File A Year-End Report",
                url: "https://www.youtube.com/watch?v=1y7a0zrvqzE&index=4&list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S&t=0s"
              }
            ]
          },
          {
            content: [
              {
                type: "link",
                text: "How To Clarify An Expenditure",
                url: "https://www.youtube.com/watch?v=SuX3Oz-i5mk&index=6&list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S&t=0s"
              }
            ]
          }
        ]
      },
      {
        type: "subheading",
        content: "Local Party Committees"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "How To File A Non-Depository Report (including the Year-End Report)",
                url: "https://www.youtube.com/watch?v=xz__TxAhjmk&index=3&list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S&t=0s"
              }
            ]
          },
          {
            content: [
              {
                type: "link",
                text: "How To Upload Data to A Report",
                url: "https://www.youtube.com/watch?v=5wNyzDIa4B4&index=12&list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S&t=0s"
              }
            ]
          }
        ]
      },
      {
        type: "subheading",
                content: "IEPACs (Super-PACs)"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "How to file a 7-day/24-hour report (IEPAC)",
                url: "https://www.youtube.com/watch?v=6khFRu7xexw&index=9&list=PLjg1OsRbioqD7UVWWB0r6-alJklot-75S&t=0s"
              }
            ]
          }
        ]
      }
    ]
  },
  {
    title: "R7 E-filing Instructions",
    blocks: [
      {
        type: "subheading",
                content: "Depository Filers"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "E-Filing Instructions",
                url: "#"
              }
            ]
          }
        ]
      },
      {
       type: "subheading",
                content: "Non Depository Filers"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "E-Filing Instructions",
                url: "#"
              }
            ]
          }
        ]
      },
      {
       type: "subheading",
                content: "Segregated Fund Accounts"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "link",
                text: "E-Filing Instructions",
                url: "#"
              }
            ]
          }
        ]
      }
    ]
  }
];


const ReporterInfo = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });


  return (
      <div>
        <Box sx={{paddingBottom: 2}}>
        <Typography
              component="h1"
              variant="h4"
              align={'left'}
              data-aos={'fade-up'}
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
              paddingTop={4}>
              How to Use OCPF Reporter 7 to Prepare and File Reports
            </Typography>

            <Divider />
            </Box>
         {content.map((content, index) => (
          <InfoItemBox key={index} {...content} />
        ))}
      </div>
       
   
        
  );
};

export default ReporterInfo;
